/**
 * Storefront Hunter models, filters, and adapters.
 */

export const COLUMN_KEYS = [
  "storefront_price",
  "buy_box_price",
  "max_cost",
  "seller_count",
  "monthly_sold",
  "rating",
  "reviews",
  "upc",
  "walmart_item_id",
  "availability",
  "quick_roi",
  "buy_sources",
];

export const COLUMN_LABELS = {
  storefront_price: "Storefront Price",
  buy_box_price: "Buy Box",
  max_cost: "Max Cost",
  seller_count: "Sellers",
  monthly_sold: "Monthly Sold",
  rating: "Rating",
  reviews: "Reviews",
  upc: "UPC",
  walmart_item_id: "Walmart Item ID",
  availability: "Availability",
  quick_roi: "Quick ROI",
  buy_sources: "Buy Sources",
};

export const DEFAULT_COLUMNS = {
  storefront_price: true,
  buy_box_price: true,
  max_cost: true,
  seller_count: true,
  monthly_sold: true,
  rating: true,
  reviews: true,
  upc: true,
  walmart_item_id: true,
  availability: true,
  quick_roi: true,
  buy_sources: true,
};

/** Legacy column keys removed from the approved Storefront Hunter design. */
const REMOVED_COLUMN_KEYS = ["avg_price", "average", "avgPrice", "averagePrice", "sales_rank"];

export const DEFAULT_FILTERS = {
  productTitle: "",
  productIds: "",
  excludeKeywords: [],
  upcList: [],
  itemIdList: [],
  brands: [],
  categories: [],
  storefrontPriceMin: "",
  storefrontPriceMax: "",
  buyBoxMin: "",
  buyBoxMax: "",
  maxCostMin: "",
  maxCostMax: "",
  monthlySoldMin: "",
  monthlySoldMax: "",
  sellersMin: "",
  sellersMax: "",
  ratingMin: "",
  reviewCountMin: "",
  reviewCountMax: "",
  newProductsOnly: false,
  newProductAge: "",
  availability: "Any",
  buyBoxStatus: "Any",
  fulfillment: "Any",
  hasBuySources: false,
};

export const FILTERS_STORAGE_KEY = "ez_storefront_hunter_saved_filters_v1";
export const COLUMNS_STORAGE_KEY = "ez_storefront_hunter_columns_v1";

export function sanitizeColumnPrefs(cols = {}) {
  const next = { ...DEFAULT_COLUMNS, ...cols };
  REMOVED_COLUMN_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}

export function loadColumnPrefs() {
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_COLUMNS };
    return sanitizeColumnPrefs(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_COLUMNS };
  }
}

export function persistColumnPrefs(cols) {
  localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(sanitizeColumnPrefs(cols)));
}

export function loadSavedFilters() {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function persistSavedFilters(list) {
  localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(list.slice(0, 20)));
}

function num(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function applyProductFilters(products, filters) {
  const titleQ = String(filters.productTitle || "").trim().toLowerCase();
  const exclude = (filters.excludeKeywords || []).map((t) => String(t).toLowerCase());
  const idTokens = [
    ...String(filters.productIds || "")
      .split(/[\s,]+/)
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean),
    ...(filters.upcList || []).map((t) => String(t).toLowerCase()),
    ...(filters.itemIdList || []).map((t) => String(t).toLowerCase()),
  ];
  const brands = new Set((filters.brands || []).map((b) => String(b).toLowerCase()));
  const cats = new Set((filters.categories || []).map((c) => String(c).toLowerCase()));

  return (products || []).filter((p) => {
    const title = String(p.title || "").toLowerCase();
    if (titleQ && !title.includes(titleQ)) return false;
    if (exclude.some((ex) => ex && title.includes(ex))) return false;
    if (idTokens.length) {
      const hay = `${p.upc || ""} ${p.walmart_item_id || ""} ${p.id || ""}`.toLowerCase();
      if (!idTokens.some((t) => hay.includes(t))) return false;
    }
    if (brands.size && !brands.has(String(p.brand || "").toLowerCase())) return false;
    if (cats.size && !cats.has(String(p.category || "").toLowerCase())) return false;

    const sp = num(p.storefront_price);
    const bb = num(p.buy_box_price);
    const mc = num(p.max_cost);
    const ms = num(p.monthly_sold);
    const sc = num(p.seller_count);
    const rt = num(p.rating);
    const rc = num(p.review_count);

    const spMin = num(filters.storefrontPriceMin);
    const spMax = num(filters.storefrontPriceMax);
    if (spMin != null && (sp == null || sp < spMin)) return false;
    if (spMax != null && (sp == null || sp > spMax)) return false;

    const bbMin = num(filters.buyBoxMin);
    const bbMax = num(filters.buyBoxMax);
    if (bbMin != null && (bb == null || bb < bbMin)) return false;
    if (bbMax != null && (bb == null || bb > bbMax)) return false;

    const mcMin = num(filters.maxCostMin);
    const mcMax = num(filters.maxCostMax);
    if (mcMin != null && (mc == null || mc < mcMin)) return false;
    if (mcMax != null && (mc == null || mc > mcMax)) return false;

    const msMin = num(filters.monthlySoldMin);
    const msMax = num(filters.monthlySoldMax);
    if (msMin != null && (ms == null || ms < msMin)) return false;
    if (msMax != null && (ms == null || ms > msMax)) return false;

    const scMin = num(filters.sellersMin);
    const scMax = num(filters.sellersMax);
    if (scMin != null && (sc == null || sc < scMin)) return false;
    if (scMax != null && (sc == null || sc > scMax)) return false;

    const rMin = num(filters.ratingMin);
    if (rMin != null && (rt == null || rt < rMin)) return false;

    const rcMin = num(filters.reviewCountMin);
    const rcMax = num(filters.reviewCountMax);
    if (rcMin != null && (rc == null || rc < rcMin)) return false;
    if (rcMax != null && (rc == null || rc > rcMax)) return false;

    const ageCap = num(filters.newProductAge);
    if (ageCap != null && !(Number(p.added_days_ago) <= ageCap)) return false;
    else if (filters.newProductsOnly && !(Number(p.added_days_ago) <= 7)) return false;

    if (filters.availability && filters.availability !== "Any") {
      const avail = String(p.availability || "").toLowerCase();
      if (filters.availability === "In stock" && !avail.includes("in stock")) return false;
      if (filters.availability === "Limited" && !avail.includes("limited")) return false;
      if (filters.availability === "Out of stock" && !avail.includes("out")) return false;
    }

    if (filters.fulfillment && filters.fulfillment !== "Any") {
      const f = String(p.fulfillment || "").toUpperCase();
      if (filters.fulfillment === "WFS" && f !== "WFS") return false;
      if (filters.fulfillment === "FBM" && f === "WFS") return false;
    }

    if (filters.hasBuySources && !(p.sources && p.sources.length)) return false;

    if (filters.buyBoxStatus && filters.buyBoxStatus !== "Any") {
      const owner = String(p.buy_box_owner || "").toLowerCase();
      if (filters.buyBoxStatus === "Walmart.com" && !owner.includes("walmart")) return false;
      if (filters.buyBoxStatus === "3P seller" && owner.includes("walmart")) return false;
    }

    return true;
  });
}

export function estimateQuickRoi(buyCost, buyBoxPrice) {
  const cost = num(buyCost);
  const bb = num(buyBoxPrice);
  if (cost == null || bb == null || cost < 0 || bb <= 0) {
    return { profit: null, roi: null, breakEven: null, netPayout: null };
  }
  // Lightweight preview only — not full fee engine.
  const referral = bb * 0.15;
  const netPayout = Math.round((bb - referral) * 100) / 100;
  const profit = Math.round((netPayout - cost) * 100) / 100;
  const roi = cost > 0 ? Math.round((profit / cost) * 1000) / 10 : null;
  const breakEven = Math.round((cost / 0.85) * 100) / 100;
  return { profit, roi, breakEven, netPayout };
}

/** Format rating to one decimal for display — does not alter source data. */
export function formatRating(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(1);
}

export function relativeAge(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export function sortProducts(products, sortKey) {
  const list = [...(products || [])];
  switch (sortKey) {
    case "price_asc":
      return list.sort((a, b) => (a.storefront_price ?? 0) - (b.storefront_price ?? 0));
    case "price_desc":
      return list.sort((a, b) => (b.storefront_price ?? 0) - (a.storefront_price ?? 0));
    case "monthly_sold":
      return list.sort((a, b) => (b.monthly_sold ?? 0) - (a.monthly_sold ?? 0));
    default:
      return list.sort((a, b) => (a.added_days_ago ?? 999) - (b.added_days_ago ?? 999));
  }
}

export function productsForSeller(allProducts, sellerId) {
  if (!sellerId) return allProducts || [];
  return (allProducts || []).filter((p) => !p.seller_id || p.seller_id === sellerId);
}

/**
 * Demo / test fixture loader only.
 * Production Analyze path uses lib/storefrontHunter/api.js — never silently
 * fall back to fixtures after an API error.
 */
export async function loadStorefrontHunterBundle({ demo = false } = {}) {
  if (!demo && typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search || "");
    demo = params.get("demo") === "1";
  }
  if (!demo) {
    return {
      store: null,
      storesBySeller: {},
      brands: [],
      categories: [],
      products: [],
      sellers: [],
      pagination: { page: 1, page_size: 25, total: null, next_cursor: null },
      dataSource: "idle",
      note: null,
    };
  }
  const {
    FIXTURE_STORE,
    FIXTURE_BRANDS,
    FIXTURE_CATEGORIES,
    FIXTURE_PRODUCTS,
    FIXTURE_SELLERS,
    FIXTURE_STORES_BY_SELLER,
    DATA_SOURCE_NOTE,
  } = await import("./fixtures");
  return {
    store: FIXTURE_STORE,
    storesBySeller: FIXTURE_STORES_BY_SELLER,
    brands: FIXTURE_BRANDS,
    categories: FIXTURE_CATEGORIES,
    products: FIXTURE_PRODUCTS,
    sellers: FIXTURE_SELLERS,
    pagination: {
      page: 1,
      page_size: 25,
      total: FIXTURE_STORE?.product_count ?? FIXTURE_PRODUCTS.length,
      next_cursor: null,
    },
    dataSource: "temporary_fixture",
    note: DATA_SOURCE_NOTE,
  };
}
